import Cookies from 'js-cookie'
import React, { FC, useContext } from 'react'
import { Messages } from './messages/types'
import { useRouter } from 'next/router'
import { UrlObject } from 'url'

type State = {
  messages: Messages
  lang: string
}

type Context = {
  setLang: (lang: string) => void
}

type ProviderContext = State & Context

const Context = React.createContext<ProviderContext>({
  messages: {} as any,
  lang: 'nl',
  setLang: (lang: string) => null
})

interface Props {
  initialLang: string
  initialMessages: Messages
}

const LocalizationProvider: FC<Props> = ({ initialLang, initialMessages, children }) => {
  const handleSetLang = async (newLang: string) => {
    if (typeof window === 'undefined') return

    // Typegaurd (only allow 'nl' | 'en')
    if (['nl', 'en'].includes(newLang)) {
      Cookies.set('user_preferred_language', newLang, { expires: 365 })
      // Get the corresponding hreflang tag and redirect to it
      // similar to how we do it at choices (https://github.com/reversed-digital/choices-by-dl/blob/master/client/components/Intl/index.tsx)
      const alternate: HTMLLinkElement | null = document.querySelector(
        newLang === 'nl' ? 'link[hreflang="nl"]' : 'link[hreflang="en"]'
      )
      if (alternate && alternate.href) {
        window.location.replace(alternate.href)
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error('No alternate hreflang found')
        }
      }
    }
  }

  return (
    <Context.Provider
      value={{
        messages: initialMessages,
        lang: initialLang,
        setLang: handleSetLang
      }}
    >
      {children}
    </Context.Provider>
  )
}

type Url = UrlObject | string
export const useLocalizedRouter = () => {
  const { lang } = useLocalization()
  const router = useRouter()
  return {
    push: (url: Url, as?: Url, options?: {}) => {
      const localizedUrl =
        typeof url === 'string' ? `/[lang]${url}` : { ...url, pathname: `/[lang]${url.pathname}` }
      const localizedAs =
        typeof as === 'string' ? `/${lang}${as}` : { ...as, pathname: `/${lang}${as.pathname}` }
      router.push(localizedUrl, localizedAs, options)
    },
    replace: (url: Url, as?: Url, options?: {}) => {
      const localizedUrl =
        typeof url === 'string' ? `/[lang]${url}` : { ...url, pathname: `/[lang]${url.pathname}` }
      const localizedAs =
        typeof as === 'string' ? `/${lang}${as}` : { ...as, pathname: `/${lang}${as.pathname}` }
      router.replace(localizedUrl, localizedAs, options)
    }
  }
}

export const useLocalization = (): ProviderContext => {
  const ctx = useContext(Context)
  if (typeof ctx === 'undefined') {
    throw new Error('Can only use useLocalization within a LocalizationProvider')
  }

  return ctx
}

export default LocalizationProvider
